import {
  Add,
  ArrowForwardIos,
  Search,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  Stack,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Typography,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import { ShieldOff, Search as SearchIcon, Clock, Pencil } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { GlowingEffect } from '@/components/ui/GlowingEffect';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { useProjectName } from '@/app/layout/ProjectNameContext';
import { useDataContext } from '@/lib/DataContext';
import { ROLE } from '@/lib/constants';

import { getAiKpiScoresSummary, type AiKpiScoreSummary } from '@/lib/api/aiKpiService';
import WelcomeOnboarding from '@/app/onboarding-secops/WelcomeOnboarding';
import { searchOnboardingsByLeanIx, type OnboardingListItem } from '@/app/onboarding-secops/api';
import { isValidAppId, normalizeAppId } from '@/app/onboarding-secops/validation';
import NerdSpinner from '@/components/NerdSpinner';

export default function ProjectExplorer() {
  const [appList, setAppList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scores, setScores] = useState<Record<string, AiKpiScoreSummary>>({});

  const dataContext = useDataContext() || {};
  const { data } = dataContext;
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { clearProject } = useProjectName();
  const hasAnySentinelRole = [ROLE.SENTINEL_USERS, ROLE.SENTINEL_PROJECT_CREATION, ROLE.SENTINEL_POWER_USER]
    .some((r) => data?.roles?.includes(r));
  // User with no Sentinel roles at all — sees the project list + Add card,
  // but clicking Add opens the Alice role request dialog instead of onboarding.
  const isNoRoleUser = !hasAnySentinelRole;
  const canCreateProjects = [ROLE.SENTINEL_PROJECT_CREATION, ROLE.SENTINEL_POWER_USER].some((r) => data?.roles?.includes(r));
  const isPlatformOwnerUser = [ROLE.SENTINEL_POWER_USER].some((r) => data?.roles?.includes(r));
  const isBaseUser = data?.roles?.includes(ROLE.SENTINEL_USERS) && !canCreateProjects;

  // No-role user: clicking Add shows Alice role request dialog
  const [noRoleDialogOpen, setNoRoleDialogOpen] = useState(false);

  // Search & Request Access dialog state (for base users)
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [accessSearchId, setAccessSearchId] = useState('APP-');
  const [accessSearching, setAccessSearching] = useState(false);
  const [accessResults, setAccessResults] = useState<OnboardingListItem[]>([]);
  const [accessSearched, setAccessSearched] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [requestingAccess, setRequestingAccess] = useState<string | null>(null);

  const handleAccessSearch = useCallback(async () => {
    if (!isValidAppId(accessSearchId)) return;
    setAccessSearching(true);
    setAccessError(null);
    try {
      const results = await searchOnboardingsByLeanIx(accessSearchId.trim());
      setAccessResults(results || []);
      setAccessSearched(true);
    } catch {
      setAccessError('Search failed. Please try again.');
      setAccessResults([]);
      setAccessSearched(true);
    } finally {
      setAccessSearching(false);
    }
  }, [accessSearchId]);

  const handleRequestAccess = useCallback(async (appId: string) => {
    setRequestingAccess(appId);
    try {
      await sentinelNetwork.post(`/config/project/${appId}/access-request`, { requestType: 'team' });
      // Refresh the project list to show the pending project
      await search();
      setAccessDialogOpen(false);
      setAccessSearchId('APP-');
      setAccessResults([]);
      setAccessSearched(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to request access.';
      setAccessError(msg);
    } finally {
      setRequestingAccess(null);
    }
  }, []);

  useEffect(() => {
    clearProject();
  }, []);

  const goToProject = (projectDetails: any) => {
    navigate(`/projects/${projectDetails.appId}`, {
      state: { projectDetails },
    });
  };

  const handleEditProject = (e: React.MouseEvent, appId: string) => {
    e.stopPropagation();
    navigate(`/onboarding/edit/${appId}`);
  };

  const sortProjects = (projects: any[]) =>
    [...projects].sort((a, b) => {
      const da = a.created_date ? new Date(a.created_date).getTime() : 0;
      const db = b.created_date ? new Date(b.created_date).getTime() : 0;
      return db - da;
    });

  const search = async () => {
    setLoading(true);
    try {
      const response = await sentinelNetwork.get('/config/projects', { params: { lean: 'true' } });
      setAppList(sortProjects(response.data.data || []));
    } catch (error) {
      console.log(error);
      setAppList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data?.user_email) {
      search();
    }
  }, [data?.user_email]);

  // Batch-fetch AI KPI scores for all projects
  useEffect(() => {
    if (appList.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const ids = appList.map((p) => p.appId);
        const { data } = await getAiKpiScoresSummary(ids);
        if (!cancelled && data?.scores) setScores(data.scores);
      } catch { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [appList]);



  if (loading) {
    return (
      <Stack
        height="400px"
        width={'100%'}
        mb={3}
        p={4}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography mt={2} variant="subtitle1" color="text.secondary">
          {t('projects.loading')}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack width="100%" p={3}>
      {Array.isArray(appList) && appList.length === 0 && !canCreateProjects && !isBaseUser && !isNoRoleUser ? (
        <Box width="100%" py={4}>
          <WelcomeOnboarding title={t('projects.noProjectsTitle')} subtitle={t('projects.noProjectsSubtitle')} />
        </Box>
      ) : (
      <>
      <TextField
        placeholder={t('projects.searchPlaceholder')}
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 1.5, maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color="action" />
            </InputAdornment>
          ),
          sx: { borderRadius: 2 },
        }}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 2,
          width: '100%',
          my: 1.5,
        }}
      >
        {(canCreateProjects || isNoRoleUser) ? (
          <Tooltip title={isNoRoleUser ? 'Request access to create projects' : t('projects.createNewTooltip')} arrow placement="top">
            <Card
              sx={{
                width: '100%',
                background: alpha(theme.palette.text.primary, 0.01),
                border: `1px dashed ${alpha(theme.palette.text.primary, 0.2)}`,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[4],
                  borderColor: theme.palette.primary.main,
                },
              }}
              onClick={() => isNoRoleUser ? setNoRoleDialogOpen(true) : navigate('/onboarding/new')}
            >
              <CardContent
                sx={{
                  alignItems: 'center',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  display: 'flex',
                  flexGrow: 1,
                  height: '100%',
                  p: 0,
                }}
              >
                <IconButton
                  sx={{
                    border: `2px dashed ${theme.palette.primary.main}`,
                    mb: 2,
                    p: 2,
                    color: theme.palette.primary.main,
                    background: alpha(theme.palette.primary.main, 0.05),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.1),
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <Add fontSize="large" />
                </IconButton>
                <Typography variant="body2" fontWeight="medium">
                  {t('projects.addProjectCard')}
                </Typography>
              </CardContent>
            </Card>
          </Tooltip>
        ) : (
          <Tooltip title="Search for a project and request access" arrow placement="top">
            <Card
              sx={{
                width: '100%',
                background: alpha(theme.palette.text.primary, 0.01),
                border: `1px dashed ${alpha(theme.palette.text.primary, 0.2)}`,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[4],
                  borderColor: theme.palette.primary.main,
                },
              }}
              onClick={() => setAccessDialogOpen(true)}
            >
              <CardContent
                sx={{
                  alignItems: 'center',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  display: 'flex',
                  flexGrow: 1,
                  height: '100%',
                  p: 0,
                }}
              >
                <IconButton
                  sx={{
                    border: `2px dashed ${theme.palette.primary.main}`,
                    mb: 2,
                    p: 2,
                    color: theme.palette.primary.main,
                    background: alpha(theme.palette.primary.main, 0.05),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.1),
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <SearchIcon size={28} />
                </IconButton>
                <Typography variant="body2" fontWeight="medium">
                  Search &amp; Request Project Access
                </Typography>
              </CardContent>
            </Card>
          </Tooltip>
        )}

        {Array.isArray(appList) &&
          appList
            ?.filter((p: any) => {
              if (!searchQuery.trim()) return true;
              const q = searchQuery.trim().toLowerCase();
              return (
                (p.appName || '').toLowerCase().includes(q) ||
                (p.leanIXId || '').toLowerCase().includes(q)
              );
            })
            .map((currentProject: any, i: number) => (
            <Card
              sx={{
                width: '100%',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'visible',
                cursor: currentProject.pendingAccess ? 'default' : 'pointer',
                opacity: currentProject.pendingAccess ? 0.75 : 1,
                '&:hover': currentProject.pendingAccess ? {} : {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[4],
                },
              }}
              key={i}
              onClick={() => { if (!currentProject.pendingAccess) goToProject(currentProject); }}
            >
              <CardHeader
                sx={{
                  pt: 2.5,
                  pb: 0.5,
                  minWidth: 0,
                  '& .MuiCardHeader-content': { minWidth: 0, overflow: 'hidden' },
                }}
                title={
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ minWidth: 0 }}>
                    <Tooltip title={currentProject.appName} arrow placement="top">
                      <Typography
                        sx={{
                          fontWeight: 200,
                          fontSize: '1.25rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {currentProject.appName}
                      </Typography>
                    </Tooltip>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                      {currentProject.pendingAccess && (
                        <Chip
                          icon={<Clock size={13} />}
                          label="Pending Access"
                          size="small"
                          sx={{
                            fontSize: '0.68rem',
                            height: 22,
                            bgcolor: alpha('#f59e0b', 0.12),
                            color: '#b45309',
                            border: '1px solid',
                            borderColor: alpha('#f59e0b', 0.3),
                            fontWeight: 600,
                          }}
                        />
                      )}
                      {currentProject.leanIXId && (
                        <Chip
                          label={currentProject.leanIXId}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 22 }}
                        />
                      )}
                    </Stack>
                  </Stack>
                }
              />

              {/* Score badge */}
              {(() => {
                const s = scores[currentProject.appId];
                const score = s?.projectScore;
                const rag = (s?.projectRag || '').toUpperCase();
                const ragColor = rag === 'GREEN' ? '#22c55e' : rag === 'AMBER' ? '#f59e0b' : rag === 'RED' ? '#ef4444' : theme.palette.text.disabled;
                return (
                  <Box sx={{ px: 2, pb: 1 }}>
                    {score != null ? (
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box sx={{
                          position: 'relative', width: 40, height: 40,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <CircularProgress
                            variant="determinate"
                            value={100}
                            size={40}
                            thickness={3}
                            sx={{ color: alpha(ragColor, 0.15), position: 'absolute' }}
                          />
                          <CircularProgress
                            variant="determinate"
                            value={Math.min(score, 100)}
                            size={40}
                            thickness={3}
                            sx={{ color: ragColor, position: 'absolute' }}
                          />
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: ragColor }}>
                            {Math.round(score)}
                          </Typography>
                        </Box>
                        <Stack spacing={0}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.primary' }}>
                            {rag === 'GREEN' ? 'Healthy' : rag === 'AMBER' ? 'Needs Attention' : rag === 'RED' ? 'Critical' : 'Score'}
                          </Typography>
                          {s?.toolCoverage && (
                            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>
                              {s.toolCoverage.connected.length} tool{s.toolCoverage.connected.length !== 1 ? 's' : ''} connected
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    ) : (
                      <Chip
                        icon={<ShieldOff size={13} />}
                        label="Not yet scanned"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.68rem', height: 24, color: 'text.secondary', borderColor: alpha(theme.palette.text.primary, 0.12) }}
                      />
                    )}
                  </Box>
                );
              })()}

              <Divider sx={{ mx: 2, opacity: 0.6 }} />

              <CardActions
                sx={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  display: 'flex',
                  px: 2,
                  pb: 2,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.7rem',
                  }}
                >
                  {currentProject.created_date && (
                    <>
                      {t('projects.created')}{' '}
                      {formatDistanceToNow(
                        new Date(currentProject.created_date),
                        {
                          addSuffix: true,
                          includeSeconds: true,
                        }
                      )}
                    </>
                  )}
                </Typography>

                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 'auto' }}>
                  {canCreateProjects && (
                    <Tooltip title={t('projects.settings', 'Project Settings')} arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); navigate(`/project/${currentProject.appId}/settings`); }}
                        sx={{
                          width: 32,
                          height: 32,
                          border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
                          color: 'text.secondary',
                          '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                        }}
                      >
                        <Pencil size={15} />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title={t('projects.openProject')} arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); navigate(`/projects/${currentProject.appId}`); }}
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: 'primary.contrastText',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                          transform: 'scale(1.1)',
                        },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <ArrowForwardIos fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardActions>

              <GlowingEffect
                spread={40}
                glow={true}
                disabled={false}
                proximity={64}
                inactiveZone={0.01}
                borderWidth={3}
              />
            </Card>
          ))}
      </Box>
      </>
      )}

      {/* Search & Request Access Dialog (base users) */}
      <Dialog open={accessDialogOpen} onClose={() => { setAccessDialogOpen(false); setAccessError(null); setAccessResults([]); setAccessSearched(false); setAccessSearchId('APP-'); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1.05rem', fontWeight: 700 }}>Search &amp; Request Project Access</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.875rem', mb: 2 }}>
            Enter the LeanIX ID of the project you want to join. Once approved by a project admin, you'll have full access.
          </DialogContentText>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <TextField
              label="LeanIX ID"
              placeholder="APP-12345"
              size="small"
              required
              value={accessSearchId}
              disabled={accessSearching}
              onChange={(e) => setAccessSearchId(normalizeAppId(e.target.value))}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAccessSearch(); }}
              error={!!accessSearchId.trim() && accessSearchId.trim() !== 'APP-' && !isValidAppId(accessSearchId)}
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleAccessSearch}
              disabled={!isValidAppId(accessSearchId) || accessSearching}
              startIcon={accessSearching ? <NerdSpinner size={16} /> : undefined}
              sx={{ textTransform: 'none' }}
            >
              {accessSearching ? 'Searching...' : 'Search'}
            </Button>
          </Stack>

          {accessError && <Alert severity="error" sx={{ mt: 2 }}>{accessError}</Alert>}

          {accessSearched && !accessError && accessResults.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>No projects found for {accessSearchId.trim()}</Alert>
          )}

          {accessResults.length > 0 && (
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {accessResults.length} project{accessResults.length !== 1 ? 's' : ''} found
              </Typography>
              {accessResults.map((item) => (
                <Card key={item.appId} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ pb: '8px !important', pt: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{item.config_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.leanIXId || item.appId}</Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={requestingAccess === item.appId}
                        onClick={() => handleRequestAccess(item.appId)}
                        startIcon={requestingAccess === item.appId ? <NerdSpinner size={14} /> : undefined}
                        sx={{ textTransform: 'none' }}
                      >
                        {requestingAccess === item.appId ? 'Requesting...' : 'Request Access'}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAccessDialogOpen(false); setAccessError(null); setAccessResults([]); setAccessSearched(false); setAccessSearchId('APP-'); }} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* No-role user: Alice role request dialog */}
      <Dialog open={noRoleDialogOpen} onClose={() => setNoRoleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Access Required</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You need a Sentinel entitlement to create projects. Choose the role that fits your needs and request it via Alice.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {[
              { name: 'APP5784.SENTINEL_USERS', title: 'Sentinel Users', description: 'View and work on projects you\'ve been added to. Trigger scans, access project settings, and view security insights.' },
              { name: 'APP5784.SENTINEL_PROJECT_CREATION', title: 'Project Creation', description: 'Everything in Sentinel Users, plus create and onboard new projects and manage project configurations.' },
              { name: 'APP5784.SENTINEL_POWER_USER', title: 'Power User', description: 'Full administrative access. Manage all projects, users, and platform-wide settings.' },
            ].map((role) => (
              <Card key={role.name} variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>{role.title}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mb: 1, color: 'text.secondary' }}>{role.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{role.description}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNoRoleDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ textTransform: 'none' }}
            href="https://alice.mercedes-benz.com/access/access-request/myself"
            target="_blank"
            rel="noopener noreferrer"
          >
            Request Access on Alice
          </Button>
        </DialogActions>
      </Dialog>

    </Stack>
  );
}
